/**
 * Agent types matching the API/database schema
 */

export type VerificationStatus = 'listed' | 'verified' | 'featured';

export interface Agent {
  id: string;
  name: string;
  description: string;
  url: string;
  category: AgentCategory;
  tags: string[];
  logo: string | null;
  ton_wallet: string | null;
  telegram_bot: string | null;
  twitter: string | null;
  vote_count: number;
  /** Whether the agent has been verified by TeleClaw */
  verified: boolean;
  /** Verification tier: listed, verified, or featured */
  verification_status: VerificationStatus;
  created_at: number;
  /** Whether the current user has voted for this agent (only when authenticated) */
  hasVoted?: boolean;
  /** Whether the current user is the owner of this agent (only when authenticated) */
  isOwner?: boolean;
  /** Referral code for this agent */
  referral_code?: string | null;
  /** Number of agents referred */
  referral_count?: number;
  /** Average review rating */
  avg_rating?: number;
  /** Number of reviews */
  review_count?: number;
  /** Whether agent has a mini app */
  has_mini_app?: boolean;
}

export interface Review {
  id: string;
  agent_id: string;
  reviewer_id: string;
  reviewer_name: string;
  rating: number;
  comment: string | null;
  is_agent_review: number;
  created_at: string;
}

export interface Collection {
  id: string;
  name: string;
  description: string | null;
  emoji: string;
  slug: string;
  is_official: number;
  agent_count?: number;
  agents?: Agent[];
}

export interface Referrer {
  id: string;
  name: string;
  logo: string | null;
  referral_count: number;
  referral_code: string;
}

export type AgentCategory =
  | 'social'
  | 'defi'
  | 'gaming'
  | 'tools'
  | 'entertainment'
  | 'ai'
  | 'nft'
  | 'trading'
  | 'infrastructure'
  | 'creative'
  | 'games'
  | 'virtual-worlds'
  | 'work-markets'
  | 'relationships';

export const CATEGORIES: { value: AgentCategory | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'defi', label: '💰 DeFi' },
  { value: 'gaming', label: '🎮 Gaming' },
  { value: 'social', label: '💬 Social' },
  { value: 'tools', label: '🛠 Tools' },
  { value: 'ai', label: '🤖 AI' },
  { value: 'nft', label: '🖼 NFT' },
  { value: 'entertainment', label: '🎭 Fun' },
];

export interface AgentListResponse {
  agents: Agent[];
  total: number;
  hasMore: boolean;
}

export interface AgentListParams {
  category?: AgentCategory;
  sort?: 'votes' | 'newest';
  page?: number;
  limit?: number;
  q?: string;
  featured?: boolean;
}

export interface CreateAgentRequest {
  name: string;
  description: string;
  url: string;
  category: AgentCategory;
  tags: string[];
  logo?: string;
  ton_wallet?: string;
  telegram_bot?: string;
  twitter?: string;
}

export interface CreateAgentResponse {
  agentId: string;
  shareUrl: string;
}

export interface VoteResponse {
  success: boolean;
  vote_count: number;
}
